import dayjs from 'dayjs';
import React from 'react'
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { UpsertAction } from './Enums';
import { Talent } from './models/dtos';
import { ApiResponse } from './models/custom-models';

declare global {
    interface String {
        splice(idx: number, rem: number, str: string): string;
    }
}

// eslint-disable-next-line no-extend-native
String.prototype.splice = function (idx, rem, str) {
    if (this.length < (idx + 1))
        return this;
    return this.slice(0, idx) + str + this.slice(idx + Math.abs(rem));
};

export const UrlFromRelative = (relativePath: string, includeBeta: boolean = true) => {
    let baseUrl = `//synergistpro.azurewebsites.net`;
    if (window.location.href.toLowerCase().includes("localhost")) {
        baseUrl = `//localhost:3000`;
    }
    let ret = `${baseUrl}/${relativePath}`;
    //console.log(ret);
    return encodeURI(ret);
}

export const GoToLocation = (relativePath: string) => {
    window.location.href = UrlFromRelative(relativePath, true);
}

export const FormatSSN = (value) => {
    return NumericDigitsOnly(value)?.slice(0, 9).splice(3, 0, '-').splice(6, 0, '-');
}
export const IsSSN = (value) => {
    let val = FormatSSN(value);
    return (val?.length === 11 && val === value);
}
export function ifTrue(test: boolean, jsx: () => any, elsejsx: () => any = () => null) {
    if (test)
        return jsx();
    else
        return elsejsx();
}
export const GuidEmpty = '00000000-0000-0000-0000-000000000000';
export const IsValidReactObj = (data) => { return typeof data === 'string' || (Array.isArray(data)) ? React.isValidElement(data[0]) : React.isValidElement(data); }
export const RealNumberCharactersOnly = (data) => {
    let hasMinus = data[0] === "-";
    var i = 0;
    let ret = data?.replace(/[^\d.-]/g, '').replace(/-+/g, '').replace(/[.%]/g, function (match) {
        return match === "." ? (i++ === 0 ? '.' : '') : '';
    });
    if (hasMinus)
        ret = `-${ret}`;
    return ret;
}

export function IsEmail(email: string) {
    if (IsEmpty(email))
        return false;
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function sleep(ms) {
    return new Promise(
        resolve => setTimeout(resolve, ms)
    );
}

export const PositiveFloatNumberCharactersOnly = (data) => { return data?.replace(/[^\d.]/g, ''); }
export const NumericDigitsOnly = (data: string) => { return data?.replace(/[^\d]/g, ''); }
export const FormatPhone = (value: string) => {
    let numOnly = NumericDigitsOnly(value);
    if (numOnly.length < 10)
        return numOnly;

    if (numOnly.length === 10) {
        return numOnly?.splice(0, 0, '(').splice(4, 0, ') ').splice(9, 0, '-');
    }

    return value;
}
export function ValidateEmail(email: string) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export const IsPhone = (value) => {
    let val = FormatPhone(value);
    return (val?.length === 12 && val === value);
}

export const IsEmpty = (val) => {
    return (IsNullOrUndefined(val) || val.length <= 0 || val === GuidEmpty) ? true : false;
};

export const IsNotEmpty = (val) => {
    return !IsEmpty(val);
};

export const IsNullOrUndefined = (val) => {
    return (val === undefined || val === null) ? true : false;
};

export const IsNotNullOrUndefined = (val) => {
    return (val === undefined || val === null) ? false : true;
};

export const AreEqual = (obj1: object, obj2: object): boolean => {
    if (IsNullOrUndefined(obj1) || IsNullOrUndefined(obj2))
        return false;
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}

export const AreNotEqual = (obj1: object, obj2: object): boolean => {
    return !AreEqual(obj1, obj2);
}

export const DownloadAsCSV = (filenameNoExtension: string, colNames: string[], data: object[], preprocessor: (name: string, value: any) => any = (name, value) => value) => {
    if (data.length === 0) {
        return;
    }
    if (IsEmpty(colNames) || colNames.length === 0)
        colNames = Object.getOwnPropertyNames(data[0]);

    let output = colNames.map(item => `"${item}"`).join(',');
    let addQuotesToStrings = (inObj) => {
        if (typeof inObj === 'string') {
            return `"${inObj}"`;
        }
        return inObj;
    }
    data.forEach(row => {
        output += "\n";
        output += colNames.map(x => addQuotesToStrings(preprocessor(x, row[x]))).join(',');
    });

    var hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(output);
    hiddenElement.target = '_blank';

    //provide the name for the CSV file to be downloaded  
    hiddenElement.download = `${filenameNoExtension}.csv`;
    hiddenElement.click();
};

export const FormatCurrency = (value: number, decimalPlaces: number = 2) => `$${value.toFixed(decimalPlaces)}`;

export const FormatDateTime: (inDate: string | number | Date | dayjs.Dayjs) => string = (inDate) => dayjs(inDate).format('MM/DD/YYYY hh:mm A');
export const FormatDate: (inDate: string | number | Date | dayjs.Dayjs) => string = (inDate) => dayjs(inDate).format('MM/DD/YYYY');
export const FormatTime: (inDate: string | number | Date | dayjs.Dayjs) => string = (inDate) => dayjs(inDate).format('hh:mm A');
export const DefaultDateTimeOffset: string = '1970-01-01T00:00:00Z';

export function GetBodyContents(htmlString: string) {
    const pattern = /<body[^>]*>((.|[\n\r])*)<\/body>/im;
    const matches = pattern.exec(htmlString);
    return matches[1];
}


export function IfTrue(test: boolean, jsx: () => JSX.Element) {
    if (test)
        return jsx();
    else
        return null;
}

export function GetUpsertIcon(mode) {
    if (mode === UpsertAction.Create) {
        return <AddCircleIcon sx={{ color: 'inherit' }} fontSize='small' />
    }
    else if (mode === UpsertAction.Update) {
        return <EditIcon sx={{ color: 'success.light' }} fontSize='small' />
    }
}

export const TalentFullName = (talent: Talent, displayFirstNameThenLast: boolean = true) => {
    if (!talent) return '';

    const firstName = talent.FirstName ?? '';
    const lastName = talent.LastName ?? '';

    if (displayFirstNameThenLast) {
        return `${firstName} ${lastName}`.trim();
    }

    if (firstName && lastName) {
        return `${lastName}, ${firstName}`;
    }

    return `${lastName}${firstName}`.trim();
}

export const CastToBoolean = (boolValue: string): boolean => {
    return (boolValue.toLowerCase() === 'true') ? true : false;
}

export const GetMinMaxValue = (value, min, max): number => {
    return Math.min(Math.max(value, min), max);
}

export const IsBetween = (value, min, max, inclusive = false): boolean => {
    if (inclusive)
        return Math.abs(min) <= value && value <= Math.abs(max);
    else
        return Math.abs(min) < value && value < Math.abs(max);
}

export const CapitalizeFirstLetter = (inStr: string, locale = navigator.language) => {
    if (IsEmpty(inStr))
        return inStr;
    var [first, ...rest] = inStr;
    return (first.toLocaleUpperCase(locale) + rest.join(''));
}

export const ExtractForSetError = (response: ApiResponse, subtitle: string, setError): boolean => {
    if (response.HasValidationErrors) {
        let message = response.Errors.join("<br />");
        setError(message, subtitle, true);
        return true;
    }
    else if (response.IsError) {
        setError(response.Errors[0], false);
        return true;
    }
    else {
        return false;
    }
}

export function EnumIntToStringArray<T>(enumObject: T): string[]{
    let stringArray: string[] = [];
    for (let enumMember in enumObject) {
        let isValueProperty = parseInt(enumMember, 10) >= 0;
        if (isValueProperty) {
            stringArray.push(enumObject[enumMember as keyof typeof enumObject] as unknown as string);
        }
    }
    return stringArray;
}

export function EnumStringToStringArray<T>(enumObject: T): string[]{
    let stringArray: string[] = [];
    for (let enumMember in enumObject) {
            stringArray.push(enumObject[enumMember as keyof typeof enumObject] as unknown as string);
    }
    return stringArray;
}


const Helpers = {};
export default Helpers;
